import React from 'react';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import { cn } from '../../../utils/cn';

const PaginationControls = ({
  currentPage,
  totalPages,
  totalItems,
  itemsPerPage,
  onPageChange,
  onItemsPerPageChange
}) => {
  const startItem = totalItems === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  const getVisiblePages = () => {
    const maxVisible = 7;
    const pages = [];
    
    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) {
        pages?.push(i);
      }
    } else {
      if (currentPage <= 4) {
        for (let i = 1; i <= 5; i++) {
          pages?.push(i);
        }
        pages?.push('...');
        pages?.push(totalPages);
      } else if (currentPage >= totalPages - 3) {
        pages?.push(1);
        pages?.push('...');
        for (let i = totalPages - 4; i <= totalPages; i++) {
          pages?.push(i);
        }
      } else {
        pages?.push(1);
        pages?.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pages?.push(i);
        }
        pages?.push('...');
        pages?.push(totalPages);
      }
    }
    
    return pages;
  };

  if (totalPages <= 1) {
    return (
      <div className="flex items-center justify-between bg-white px-6 py-3 border-t border-border">
        <div className="text-sm text-muted-foreground">
          Exibindo {totalItems} usuário{totalItems !== 1 ? 's' : ''}
        </div>
        <div className="flex items-center space-x-2">
          <span className="text-sm text-muted-foreground">Usuários por página:</span>
          <Select
            value={itemsPerPage?.toString()}
            onValueChange={(value) => onItemsPerPageChange?.(parseInt(value))}
            className="w-20"
          >
            <Select.Item value="10">10</Select.Item>
            <Select.Item value="25">25</Select.Item>
            <Select.Item value="50">50</Select.Item>
            <Select.Item value="100">100</Select.Item>
          </Select>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white border border-border rounded-lg">
      <div className="flex flex-col sm:flex-row items-center justify-between px-6 py-4 gap-4">
        {/* Results Info */}
        <div className="text-sm text-muted-foreground">
          Exibindo <span className="font-medium">{startItem}</span> a{' '}
          <span className="font-medium">{endItem}</span> de{' '}
          <span className="font-medium">{totalItems}</span> usuários
        </div>

        {/* Pagination Controls */}
        <div className="flex items-center space-x-2">
          {/* Previous Button */}
          <Button
            variant="outline"
            size="sm"
            onClick={() => onPageChange?.(currentPage - 1)}
            disabled={currentPage === 1}
            iconName="ChevronLeft"
            className="px-3"
          >
            Anterior
          </Button>

          {/* Page Numbers */}
          <div className="flex items-center space-x-1">
            {getVisiblePages()?.map((page, index) => (
              <React.Fragment key={index}>
                {page === '...' ? (
                  <span className="px-3 py-2 text-sm text-muted-foreground">...</span>
                ) : (
                  <Button
                    variant={currentPage === page ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => onPageChange?.(page)}
                    className={cn(
                      'px-3 py-2 text-sm min-w-[2.5rem]',
                      currentPage === page && 'font-medium'
                    )}
                  >
                    {page}
                  </Button>
                )}
              </React.Fragment>
            ))}
          </div>

          {/* Next Button */}
          <Button
            variant="outline"
            size="sm"
            onClick={() => onPageChange?.(currentPage + 1)}
            disabled={currentPage === totalPages}
            iconName="ChevronRight"
            className="px-3"
          >
            Próxima
          </Button>
        </div>

        {/* Items Per Page */}
        <div className="flex items-center space-x-2">
          <span className="text-sm text-muted-foreground">Usuários por página:</span>
          <Select
            value={itemsPerPage?.toString()}
            onValueChange={(value) => onItemsPerPageChange?.(parseInt(value))}
            className="w-20"
          >
            <Select.Item value="10">10</Select.Item>
            <Select.Item value="25">25</Select.Item>
            <Select.Item value="50">50</Select.Item>
            <Select.Item value="100">100</Select.Item>
          </Select>
        </div>
      </div>
    </div>
  );
};

export default PaginationControls;